import { Injectable } from '@angular/core';
import { StateToken, State, StateContext, Selector, Action, Store } from '@ngxs/store';
import { tap } from 'rxjs';
import { UserRiskAssessmentRoutes } from 'src/app/core/enums/routes.enum';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { MenuTreeStructureState } from 'src/app/core/state/menu-tree-structure.state';
import {
  IApiResponseGroupRiskAssessments,
  IRiskAssessmentGroupData,
  IRiskAssessmentLogVersion,
  IRiskAssessmentResponse,
} from '../models/risk-assessment.model';
import { RiskAssessmentApiService } from '../services/risk-assessment-api.service';
import {
  LoadRiskAssessmentById,
  CreateRiskAssessment,
  UpdateRiskAssessment,
  DeleteRiskAssessment,
  LoadRiskAssessments,
  PerformRiskAssessment,
  LoadRiskAssessmentVersionById,
  LoadRiskAssessmentVersions,
  LoadRiskAssessmentsForIO,
  ClearRiskAssessmentsByMenu,
  ClearCurrentRiskAssessment,
  ClearCurrentRiskAssessmentVersion,
} from './risk-assessment.actions';

export interface RiskAssessmentStateModel {
  riskAssessments: IdName[];
  groupedRiskAssessments: IRiskAssessmentGroupData[];
  riskAssessmentsTotalPages: number;
  currentRiskAssessment: IRiskAssessmentResponse;
  riskAssessmentVersions: IRiskAssessmentLogVersion[];
  currentRiskAssessmentVersion: IRiskAssessmentResponse;
  riskAssessmentsForIO: IdName[];
  lastCreatedAssessment: IdName | undefined;
  lastPerformedId: string | undefined;
}

const SHIFT_STATE_TOKEN = new StateToken<RiskAssessmentStateModel>('riskAssessments');

@State<RiskAssessmentStateModel>({
  name: SHIFT_STATE_TOKEN,
  defaults: {
    riskAssessments: [],
    groupedRiskAssessments: [],
    riskAssessmentsTotalPages: 0,
    currentRiskAssessment: <IRiskAssessmentResponse>{},
    riskAssessmentVersions: [],
    currentRiskAssessmentVersion: <IRiskAssessmentResponse>{},
    riskAssessmentsForIO: [],
    lastCreatedAssessment: undefined,
    lastPerformedId: undefined,
  },
})
@Injectable()
export class RiskAssessmentState {
  constructor(
    private service: RiskAssessmentApiService,
    private store: Store
  ) {}

  @Action(LoadRiskAssessments, { cancelUncompleted: true })
  loadRiskAssessments(
    { getState, setState }: StateContext<RiskAssessmentStateModel>,
    { filter, menuId }: LoadRiskAssessments
  ) {
    return this.service.getRiskAssessments(filter, menuId).pipe(
      tap((response: IApiResponseGroupRiskAssessments) => {
        const state = getState();
        setState({
          ...state,
          groupedRiskAssessments: response.groupData,
          riskAssessmentsTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadRiskAssessmentById, { cancelUncompleted: true })
  loadRiskAssessmentById(
    { getState, setState }: StateContext<RiskAssessmentStateModel>,
    { id }: LoadRiskAssessmentById
  ) {
    return this.service.getRiskAssessmentById(id).pipe(
      tap((response: IRiskAssessmentResponse) => {
        const state = getState();
        setState({
          ...state,
          currentRiskAssessment: response,
        });
      })
    );
  }

  @Action(LoadRiskAssessmentsForIO)
  loadDocumentsForIO({ getState, setState }: StateContext<RiskAssessmentStateModel>) {
    return this.service.getRiskAssessmentsForIO().pipe(
      tap((response: IdName[]) => {
        const state = getState();
        setState({
          ...state,
          riskAssessmentsForIO: response,
        });
      })
    );
  }

  @Action(CreateRiskAssessment, { cancelUncompleted: true })
  @OnSuccess({ message: 'riskAssessmentCreated' })
  createRiskAssessment(
    ctx: StateContext<RiskAssessmentStateModel>,
    { riskAssessment, dialogId }: CreateRiskAssessment
  ) {
    return this.service.createRiskAssessment(riskAssessment).pipe(
      tap((created) => {
        ctx.patchState({ lastCreatedAssessment: created });
        if (dialogId) {
          return;
        }
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ??
          riskAssessment.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserRiskAssessmentRoutes.List}/${menuId}`);
      })
    );
  }

  @Action(UpdateRiskAssessment, { cancelUncompleted: true })
  @OnSuccess({ message: 'riskAssessmentUpdated' })
  updateRiskAssessment(
    ctx: StateContext<RiskAssessmentStateModel>,
    { riskAssessment, dialogId }: UpdateRiskAssessment
  ) {
    return this.service.updateRiskAssessment(riskAssessment).pipe(
      tap(() => {
        if (dialogId) {
          return;
        }
        const menuId =
          this.store.selectSnapshot(MenuTreeStructureState.getSelectedMenuId) ??
          riskAssessment.menuId;
        redirectAndBypassUnsavedDataCheck(`${UserRiskAssessmentRoutes.List}/${menuId}`);
      })
    );
  }

  @Action(DeleteRiskAssessment, { cancelUncompleted: true })
  @OnSuccess({ message: 'riskAssessmentDeleted', dispatch: ResetCurrentFilter })
  deleteRiskAssessment(
    ctx: StateContext<RiskAssessmentStateModel>,
    { riskAssessmentId }: DeleteRiskAssessment
  ) {
    return this.service.deleteRiskAssessment(riskAssessmentId).pipe(
      tap(() => {
        redirectAndBypassUnsavedDataCheck(
          `${UserRiskAssessmentRoutes.List}/${ctx.getState().currentRiskAssessment.menu}`
        );
      })
    );
  }

  @Action(PerformRiskAssessment, { cancelUncompleted: true })
  @OnSuccess({ message: 'riskAssessmentPerformed' })
  performRiskAssessment(
    ctx: StateContext<RiskAssessmentStateModel>,
    { performRequest, dialogId }: PerformRiskAssessment
  ) {
    return this.service.performRiskAssessment(performRequest).pipe(
      tap((versionId) => {
        ctx.patchState({ lastPerformedId: versionId });
        if (dialogId) {
          return;
        }
        redirectAndBypassUnsavedDataCheck(
          `${UserRiskAssessmentRoutes.List}/${ctx.getState().currentRiskAssessment.menu}`
        );
      })
    );
  }

  @Action(LoadRiskAssessmentVersions, { cancelUncompleted: true })
  loadRiskAssessmentVersions(
    { getState, setState }: StateContext<RiskAssessmentStateModel>,
    { id }: LoadRiskAssessmentVersions
  ) {
    return this.service.getRiskAssessmentVersions(id).pipe(
      tap((response: IRiskAssessmentLogVersion[]) => {
        const state = getState();
        setState({
          ...state,
          riskAssessmentVersions: response,
        });
      })
    );
  }

  @Action(LoadRiskAssessmentVersionById, { cancelUncompleted: true })
  loadRiskAssessmentVersionById(
    { getState, setState }: StateContext<RiskAssessmentStateModel>,
    { id, versionId }: LoadRiskAssessmentVersionById
  ) {
    return this.service.getRiskAssessmentVersionById(id, versionId).pipe(
      tap((response: IRiskAssessmentResponse) => {
        const state = getState();
        setState({
          ...state,
          currentRiskAssessmentVersion: response,
        });
      })
    );
  }

  @Action(ClearRiskAssessmentsByMenu, { cancelUncompleted: true })
  clearRiskAssessmentsByMenu(ctx: StateContext<RiskAssessmentStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      groupedRiskAssessments: [],
      riskAssessmentsTotalPages: 0,
    });
  }

  @Action(ClearCurrentRiskAssessment, { cancelUncompleted: true })
  clearCurrentRiskAssessment(ctx: StateContext<RiskAssessmentStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentRiskAssessment: <IRiskAssessmentResponse>{},
    });
  }

  @Action(ClearCurrentRiskAssessmentVersion, { cancelUncompleted: true })
  clearCurrentRiskAssessmentVersion(ctx: StateContext<RiskAssessmentStateModel>) {
    ctx.setState({
      ...ctx.getState(),
      currentRiskAssessmentVersion: <IRiskAssessmentResponse>{},
    });
  }

  @Selector()
  static getRiskAssessmentsTotalPages(state: RiskAssessmentStateModel) {
    return state.riskAssessmentsTotalPages;
  }

  @Selector()
  static getRiskAssessments(state: RiskAssessmentStateModel): IRiskAssessmentGroupData[] {
    return state.groupedRiskAssessments;
  }

  @Selector()
  static getRiskAssessmentById(state: any) {
    return state.currentRiskAssessment;
  }

  @Selector()
  static getRiskAssessmentVersions(state: any): IRiskAssessmentLogVersion[] {
    return state.riskAssessmentVersions;
  }

  @Selector()
  static getRiskAssessmentVersionById(state: any) {
    return state.currentRiskAssessmentVersion;
  }

  @Selector()
  static getRiskAssessmentsForIO(state: any): IdName[] {
    return state.riskAssessmentsForIO;
  }

  @Selector()
  static getLastCreatedAssessment(state: RiskAssessmentStateModel): IdName | undefined {
    return state.lastCreatedAssessment;
  }

  @Selector()
  static getLastPerformedId(state: RiskAssessmentStateModel): string | undefined {
    return state.lastPerformedId;
  }
}
